"use client";

import React, { useState, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { SlidersHorizontal } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchProductCategories } from "@/store/slices/shopSlice";

interface ProductFilterSidebarProps {
  className?: string;
}

const ProductFilterSidebarInner = ({
  className = "",
}: ProductFilterSidebarProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();

  const { data: allCategories } = useAppSelector(
    (state) => state.shop.categories,
  );

  useEffect(() => {
    if (!allCategories || allCategories.length === 0) {
      dispatch(fetchProductCategories());
    }
  }, [dispatch, allCategories]);

  // Read current filter state from URL
  const selectedCategory = searchParams.get("category") || "all";
  const currentMaxPrice = searchParams.get("maxPrice")
    ? Number(searchParams.get("maxPrice"))
    : 5000;

  const [priceRange, setPriceRange] = useState<number>(currentMaxPrice);

  useEffect(() => {
    setPriceRange(currentMaxPrice);
  }, [currentMaxPrice]);

  const updateFilters = (newCategory?: string, newMaxPrice?: number) => {
    const params = new URLSearchParams(searchParams.toString());

    const cat = newCategory !== undefined ? newCategory : selectedCategory;
    if (cat && cat !== "all") {
      params.set("category", cat);
    } else {
      params.delete("category");
    }

    const price = newMaxPrice !== undefined ? newMaxPrice : priceRange;
    if (price && price < 5000) {
      params.set("maxPrice", String(price));
    } else {
      params.delete("maxPrice");
    }

    // Reset pagination to page 1
    params.delete("page");

    router.replace(`?${params.toString()}`, { scroll: false });
  };

  const handleCategoryChange = (catId: string) => {
    updateFilters(catId, undefined);
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value);
    setPriceRange(val);
  };

  const handlePriceChangeComplete = () => {
    updateFilters(undefined, priceRange);
  };

  const clearAllFilters = () => {
    setPriceRange(5000);
    router.replace("?", { scroll: false });
  };

  return (
    <div
      className={`bg-white rounded-lg p-6 shadow-sm border border-gray-200/80 ${className}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <SlidersHorizontal className="text-purple-600" size={24} />
        <h2 className="text-xl font-bold text-gray-800">Filters</h2>
      </div>

      {/* Category Filter */}
      <div className="mb-6">
        <h3 className="font-semibold text-gray-800 mb-3 text-base">Category</h3>
        <div className="space-y-2.5">
          <label className="flex cursor-pointer items-center gap-2.5 text-sm select-none">
            <input
              type="radio"
              name="category"
              checked={selectedCategory === "all"}
              onChange={() => handleCategoryChange("all")}
              className="w-4 h-4 text-purple-600 focus:ring-purple-500 cursor-pointer accent-purple-600"
            />
            <span
              className={
                selectedCategory === "all"
                  ? "font-semibold text-gray-900"
                  : "text-gray-700"
              }
            >
              All Categories
            </span>
          </label>
          {allCategories.map((category) => {
            const isChecked =
              selectedCategory === category._id ||
              selectedCategory === category.slug ||
              selectedCategory === category.name;
            return (
              <label
                key={category._id}
                className="flex cursor-pointer items-center gap-2.5 text-sm select-none"
              >
                <input
                  type="radio"
                  name="category"
                  checked={isChecked}
                  onChange={() => handleCategoryChange(category._id)}
                  className="w-4 h-4 text-purple-600 focus:ring-purple-500 cursor-pointer accent-purple-600"
                />
                <span
                  className={
                    isChecked ? "font-semibold text-gray-900" : "text-gray-700"
                  }
                >
                  {category.name}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Price Range Filter */}
      <div className="mb-6">
        <h3 className="font-semibold text-gray-800 mb-3 text-base">
          Price Range
        </h3>
        <input
          type="range"
          min="0"
          max="5000"
          step="50"
          value={priceRange}
          onChange={handlePriceChange}
          onMouseUp={handlePriceChangeComplete}
          onTouchEnd={handlePriceChangeComplete}
          className="w-full accent-purple-600 cursor-pointer h-2 bg-gray-200 rounded-lg"
        />
        <div className="flex justify-between text-sm text-gray-600 mt-2 font-medium">
          <span>PKR 0</span>
          <span>PKR {priceRange.toLocaleString()}</span>
        </div>
      </div>

      {/* Clear All Filters Button */}
      <button
        type="button"
        onClick={clearAllFilters}
        className="w-full py-3 cursor-pointer border-2 border-purple-600 text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition-colors"
      >
        Clear All Filters
      </button>
    </div>
  );
};

const ProductFilterSidebar = ({
  className = "",
}: ProductFilterSidebarProps) => (
  <Suspense fallback={null}>
    <ProductFilterSidebarInner className={className} />
  </Suspense>
);

export default ProductFilterSidebar;
